import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as XLSX from 'xlsx';
import {
  Button, Checkbox, Input, InputNumber, Select, Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import CiPageChrome from '../../components/CustomerIdentity/CiPageChrome';
import { CIInfoBar } from '../../components/CustomerIdentity/CustomerIdentityShared';
import CustomerImportMappingCanvas, { computeMappingValidation } from '../../components/CustomerIdentity/CustomerImportMappingCanvas';
import CustomerImportProgressPanel from '../../components/CustomerIdentity/CustomerImportProgressPanel';
import CustomerImportJobsPanel from '../../components/CustomerIdentity/CustomerImportJobsPanel';
import SyncProfilesPanel from '../../components/CustomerIdentity/SyncProfilesPanel';
import ReindexPanel from '../../components/CustomerIdentity/ReindexPanel';
import SyncConflictsPanel from '../../components/CustomerIdentity/SyncConflictsPanel';
import { listSites } from '../../services/customerIdentityApi';
import {
  createSyncProfile, exportCustomerData, getCustomerImportJob, startCustomerImportJob,
} from '../../services/customerImportApi';

const ACTIVE_JOB_STATUSES = ['queued', 'running'];
const POLL_INTERVAL_MS = 1500;

const { Dragger } = Upload;

// Mirrors ContractorsHub/UploadPartNumbersModal.jsx's parseSpreadsheet exactly
// (Story 2.1 Dev Notes) -- xlsx reads both .csv and .xlsx uniformly.
const parseSpreadsheet = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: false });
  const sheetName = workbook?.SheetNames?.[0];
  if (!sheetName) return { headers: [], rows: [] };
  const sheet = workbook.Sheets[sheetName];
  const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  const nonEmptyRows = allRows.filter((row) => row.some((cell) => String(cell || '').trim()));
  if (!nonEmptyRows.length) return { headers: [], rows: [] };
  const [headerRow, ...dataRows] = nonEmptyRows;
  const headers = headerRow.map((cell) => String(cell || '').trim());
  return { headers, rows: dataRows };
};

export default function CustomerDataImport() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(undefined);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saveAsSyncProfile, setSaveAsSyncProfile] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profilesRefreshKey, setProfilesRefreshKey] = useState(0);
  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [job, setJob] = useState(null); // {job_id, status, phase, progress, error}
  const pollRef = useRef(null);

  useEffect(() => {
    listSites(false).then(setSites).catch((err) => {
      setMessage({ kind: 'danger', text: err.message || 'Could not load sites.' });
    });
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Stop polling if the admin navigates away mid-import -- the import keeps
  // running server-side regardless; this just stops this unmounted page
  // from leaking a timer.
  useEffect(() => () => stopPolling(), [stopPolling]);

  const finishJob = useCallback(async (finishedJob) => {
    stopPolling();
    setJob(finishedJob);
    setJobsRefreshKey((k) => k + 1);
    if (finishedJob.status === 'failed') {
      setMessage({ kind: 'danger', text: finishedJob.error || 'Import failed.' });
      return;
    }
    setSummary(finishedJob.summary);
    setMessage({ kind: 'success', text: 'Import complete.' });

    if (saveAsSyncProfile) {
      setSavingProfile(true);
      try {
        await createSyncProfile({
          site_id: siteId,
          source_url: sourceUrl,
          mapping,
          interval_minutes: intervalMinutes,
          enabled: true,
        });
        setProfilesRefreshKey((k) => k + 1);
        setMessage({ kind: 'success', text: 'Import complete. Sync profile saved.' });
      } catch (err) {
        setMessage({ kind: 'danger', text: `Import complete, but saving the sync profile failed: ${err.message}` });
      } finally {
        setSavingProfile(false);
      }
    }
  }, [saveAsSyncProfile, siteId, sourceUrl, mapping, intervalMinutes, stopPolling]);

  const pollJob = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const latest = await getCustomerImportJob(jobId);
        setJob(latest);
        if (!ACTIVE_JOB_STATUSES.includes(latest.status)) {
          await finishJob(latest);
        }
      } catch (err) {
        stopPolling();
        setMessage({ kind: 'danger', text: err.message || 'Lost track of the import job.' });
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, finishJob]);

  const handleFile = async (file) => {
    setMessage(null);
    setSummary(null);
    setJob(null);
    stopPolling();
    try {
      const parsed = await parseSpreadsheet(file);
      if (!parsed.headers.length) {
        setMessage({ kind: 'danger', text: 'That file has no readable header row.' });
        return false;
      }
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping({});
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Could not read that file.' });
    }
    return false; // prevent antd's own upload POST -- we parse client-side only
  };

  const { isValid: mappingIsValid } = computeMappingValidation(mapping);

  const handleSubmit = async () => {
    setStarting(true);
    setMessage(null);
    setSummary(null);
    setJob(null);
    try {
      const started = await startCustomerImportJob({
        site_id: siteId, headers, rows, mapping,
      });
      // The start response is just {job_id, status, totals} -- seed the
      // panel's per-entity totals immediately so it doesn't flash "no rows
      // mapped" for the ~1.5s until the first poll tick fills in real
      // phase/progress (a resumed 409 job already has a full progress blob).
      setJob(started.resumed ? started : { ...started, progress: { totals: started.totals } });
      setJobsRefreshKey((k) => k + 1);
      if (started.resumed) {
        setMessage({ kind: 'info', text: 'An import is already running for this site -- showing its progress.' });
      }
      if (ACTIVE_JOB_STATUSES.includes(started.status)) {
        pollJob(started.job_id);
      } else {
        await finishJob(started);
      }
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Could not start the import.' });
    } finally {
      setStarting(false);
    }
  };

  const jobIsActive = Boolean(job) && ACTIVE_JOB_STATUSES.includes(job.status);

  const handleExport = async () => {
    setExporting(true);
    setMessage(null);
    try {
      const { headers: exportHeaders, rows: exportRows } = await exportCustomerData({ site_id: siteId });
      const siteName = sites.find((s) => s.id === siteId)?.name || siteId;
      const sheet = XLSX.utils.aoa_to_sheet([exportHeaders, ...exportRows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Customers');
      XLSX.writeFile(workbook, `customer-data-${siteName}.xlsx`);
    } catch (err) {
      setMessage({ kind: 'danger', text: err.message || 'Export failed.' });
    } finally {
      setExporting(false);
    }
  };

  const canSaveAsSyncProfile = !saveAsSyncProfile || Boolean(sourceUrl.trim());
  const canSubmit = Boolean(siteId) && headers.length > 0 && mappingIsValid && canSaveAsSyncProfile && !starting && !jobIsActive;

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={message} onDismiss={() => setMessage(null)} />
      <CiPageChrome
        title="Customer Data Import"
        subtitle="Import a Customer (with its Bill-To Address), then its Ship-To Address, then its Contacts, from a CSV/XLSX export."
      >
        <div className="container-fluid px-0 px-md-1">
          <div className="ci-card ci-card-flush p-3 mb-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label small" htmlFor="customer-import-site">Site</label>
                <Select
                  id="customer-import-site"
                  style={{ width: '100%' }}
                  placeholder="Choose a site"
                  value={siteId}
                  onChange={setSiteId}
                  disabled={jobIsActive}
                  options={sites.map((s) => ({ value: s.id, label: s.name }))}
                />
              </div>
              <div className="col-12 col-md-5">
                <Dragger
                  multiple={false}
                  accept=".csv,.xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleFile}
                  disabled={jobIsActive}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">
                    {fileName || 'Click or drag a CSV/XLSX file to this area'}
                  </p>
                </Dragger>
              </div>
              <div className="col-12 col-md-3">
                <Button
                  block
                  onClick={handleExport}
                  loading={exporting}
                  disabled={!siteId}
                >
                  Export current data
                </Button>
                <div className="small text-muted mt-1">
                  Downloads an XLSX in the same format Import expects, for editing and re-upload.
                </div>
              </div>
            </div>
          </div>

          {headers.length > 0 && (
            <div className="ci-card ci-card-flush p-3 mb-3">
              <CustomerImportMappingCanvas headers={headers} mapping={mapping} onChange={setMapping} />

              <div className="mt-3 pt-3 border-top">
                <Checkbox checked={saveAsSyncProfile} onChange={(e) => setSaveAsSyncProfile(e.target.checked)}>
                  Save as a scheduled Sync Profile (automatically re-runs this import on an interval)
                </Checkbox>
                {saveAsSyncProfile && (
                  <div className="row g-2 mt-1">
                    <div className="col-12 col-md-8">
                      <label className="form-label small" htmlFor="sync-profile-source-url">Source URL</label>
                      <Input
                        id="sync-profile-source-url"
                        placeholder="https://.../customers.csv"
                        value={sourceUrl}
                        onChange={(e) => setSourceUrl(e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small" htmlFor="sync-profile-interval">Interval (minutes)</label>
                      <InputNumber
                        id="sync-profile-interval"
                        style={{ width: '100%' }}
                        min={5}
                        value={intervalMinutes}
                        onChange={setIntervalMinutes}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="primary"
                onClick={handleSubmit}
                loading={starting || savingProfile}
                disabled={!canSubmit}
                style={{ marginTop: 16 }}
              >
                {jobIsActive ? 'Import running…' : `Import ${rows.length} row${rows.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          )}

          {job && (
            <CustomerImportProgressPanel
              status={job.status}
              phase={job.phase}
              progress={job.progress}
              error={job.error}
            />
          )}

          {summary && (
            <div className="ci-card ci-card-flush p-3">
              <h5>Import Summary</h5>
              {[
                { key: 'customers', label: 'Customers' },
                { key: 'billtos', label: 'Bill-To Addresses' },
                { key: 'addresses', label: 'Ship-To Addresses' },
                { key: 'contacts', label: 'Contacts' },
              ].map(({ key, label }) => (
                <div key={key} className="small mb-1">
                  <strong>{label}:</strong>{' '}
                  {summary[key]?.created ?? 0} created, {summary[key]?.updated ?? 0} updated, {summary[key]?.skipped ?? 0} skipped
                </div>
              ))}
              {summary.indexed && (
                <div className="small text-muted mb-2">
                  Indexed into search: {summary.indexed.customers} customers, {summary.indexed.shipto} ship-to addresses
                </div>
              )}
              {summary.errors?.length > 0 && (
                <div className="small text-danger mt-2">
                  {summary.errors.length} error{summary.errors.length === 1 ? '' : 's'}:
                  <ul className="mb-0">
                    {summary.errors.slice(0, 20).map((e, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={idx}>{e.entity}: {e.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="ci-card ci-card-flush p-3 mt-3">
            <h5>Import Jobs</h5>
            <CustomerImportJobsPanel sites={sites} refreshKey={jobsRefreshKey} />
          </div>

          <div className="ci-card ci-card-flush p-3 mt-3">
            <h5>Sync Profiles</h5>
            <SyncProfilesPanel key={profilesRefreshKey} />
          </div>

          <div className="ci-card ci-card-flush p-3 mt-3">
            <h5>OpenSearch Reindex</h5>
            <ReindexPanel />
          </div>

          <div className="ci-card ci-card-flush p-3 mt-3">
            <h5>Sync Conflicts</h5>
            <SyncConflictsPanel />
          </div>
        </div>
      </CiPageChrome>
    </div>
  );
}
